import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { shortDayFormat } from "utils";
import { ENVS, projectTypesData } from "config";
import { US_STATES } from "config/states";
import type { ProjectDataType } from "store/types/project";
import type { FormData } from "components/common/CreateProjectSheet/CreateProjectSheet.types";
import { createProjectSchema } from "components/common/CreateProjectSheet/CreateProjectSheet.schema";
import { MIN_BUDGET_VALUE } from "components/common/CreateProjectSheet/CreateProjectSheet.constants";
import type { ProjectCreateSource } from "components/common/CreateProjectFlow/types/project-create-source.types";

type UseCreateProjectFormOptions = {
  mode: "project" | "version";
  existingProject?: Partial<ProjectDataType>;
  /** When set, "blank" uses hidden defaults so only the name is required in the UI. */
  projectCreateSource?: ProjectCreateSource | null;
  /** OTP create without an unused license: match backend (no AI credits) — turn features off. */
  otpDraftWithoutAvailableLicense?: boolean;
};

const getFirstActiveProductionTypeKey = (): string => {
  const first = projectTypesData.find((t) => t.active);
  return first?.key ?? "feature";
};

export function getCreateProjectDefaultValues({
  mode,
  existingProject,
  projectCreateSource,
  otpDraftWithoutAvailableLicense = false,
}: UseCreateProjectFormOptions): FormData {
  const isDev = process.env.NODE_ENV === "development" && !ENVS.onPrem;
  const scheduleDefault = dayjs().add(1, "day").format(shortDayFormat);

  const otpUnlicensedDraftFeatures = (): FormData["features"] => ({
    has_budget: false,
    max_budget: undefined,
    has_coverage: false,
    has_character_breakdown: false,
    has_script_breakdown: false,
    has_schedule: false,
    schedule_start_date: scheduleDefault,
    production_state: "",
  });

  if (mode === "project" && otpDraftWithoutAvailableLicense && projectCreateSource === "import") {
    return {
      name: "",
      description: "",
      // Prefilled like every other path: production type is a labelling choice, not an
      // entitlement, and leaving it empty made the form invalid before the user touched it.
      production_type: getFirstActiveProductionTypeKey(),
      // The scene breakdown is free on unlicensed projects (spaCy, no paid model).
      // The server enforces the same rule, so this only sets the default.
      features: { ...otpUnlicensedDraftFeatures(), has_script_breakdown: true },
    };
  }

  if (mode === "project" && projectCreateSource === "blank") {
    return {
      name: "",
      description: "",
      production_type: getFirstActiveProductionTypeKey(),
      features: otpDraftWithoutAvailableLicense
        ? otpUnlicensedDraftFeatures()
        : {
            has_budget: true,
            max_budget: MIN_BUDGET_VALUE,
            has_coverage: true,
            has_character_breakdown: true,
            has_script_breakdown: true,
            has_schedule: true,
            schedule_start_date: scheduleDefault,
            production_state: "",
          },
    };
  }

  return {
    name: mode === "version" && existingProject ? existingProject?.name || "" : "",
    description: mode === "version" && existingProject ? existingProject?.description || "" : "",
    production_type:
      mode === "version" && existingProject
        ? (existingProject as { production_type?: string }).production_type ||
          getFirstActiveProductionTypeKey()
        : getFirstActiveProductionTypeKey(),
    features: {
      has_budget:
        mode === "version" && existingProject
          ? Boolean(
              (existingProject as { features?: { has_budget?: boolean } }).features?.has_budget
            )
          : isDev
            ? false
            : true,
      // Seeded whenever budgeting is on, because the schema requires it in that case. Leaving
      // it undefined made the import branch fail validation with no way for the user to see why.
      max_budget:
        mode === "version" && existingProject
          ? (existingProject as { features?: { max_budget?: number } }).features?.max_budget ||
            MIN_BUDGET_VALUE
          : isDev
            ? undefined
            : MIN_BUDGET_VALUE,
      has_coverage:
        mode === "version" && existingProject
          ? Boolean(
              (existingProject as { features?: { has_coverage?: boolean } }).features?.has_coverage
            )
          : isDev
            ? false
            : true,
      has_character_breakdown:
        mode === "version" && existingProject
          ? Boolean(
              (existingProject as { features?: { has_character_breakdown?: boolean } }).features
                ?.has_character_breakdown
            )
          : isDev
            ? false
            : true,
      has_script_breakdown:
        mode === "version" && existingProject
          ? Boolean(
              (existingProject as { features?: { has_script_breakdown?: boolean } }).features
                ?.has_script_breakdown
            )
          : isDev
            ? false
            : true,
      has_schedule:
        mode === "version" && existingProject
          ? Boolean(
              (existingProject as { features?: { has_schedule?: boolean } }).features?.has_schedule
            )
          : isDev
            ? false
            : true,
      schedule_start_date:
        mode === "version" && existingProject
          ? (existingProject as { features?: { schedule_start_date?: string } }).features
              ?.schedule_start_date || scheduleDefault
          : scheduleDefault,
      production_state:
        mode === "version" && existingProject
          ? (() => {
              const stateName = (existingProject as { features?: { production_state?: string } })
                .features?.production_state;
              if (!stateName) return "";
              let stateOption = US_STATES.find((state) => state.value === stateName);
              if (stateOption) return stateOption.value;
              stateOption = US_STATES.find((state) => state.label === stateName);
              return stateOption ? stateOption.value : "";
            })()
          : "",
    },
  };
}

export function useCreateProjectForm({
  mode,
  existingProject,
  projectCreateSource,
  otpDraftWithoutAvailableLicense,
}: UseCreateProjectFormOptions) {
  const defaultValues = useMemo(
    () =>
      getCreateProjectDefaultValues({
        mode,
        existingProject,
        projectCreateSource,
        otpDraftWithoutAvailableLicense,
      }),
    [mode, existingProject, projectCreateSource, otpDraftWithoutAvailableLicense]
  );

  const methods = useForm<FormData>({
    resolver: yupResolver(createProjectSchema),
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  return { methods, defaultValues };
}
